import { useState } from "react";
import styled from "styled-components";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "../../../../../components/button";
import RadioGroup from "./radio-group";
import CheckboxGroup from "./checkbox-group";
import LinearScale from "./linear-scale";
import { Toggle } from "./toggle";
import DeleteIcon from "@material-ui/icons/DeleteForever";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { v4 as uuidv4 } from "uuid";
import TextInput from "./text-input";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const Container = styled.div`
  background: #fff;
  border: ${(props) =>
    props.selected
      ? `1px solid ${props.theme.color.primary}`
      : "1px solid #FFF"};
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  cursor: ${(props) => (props.selected ? "default" : "pointer")};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  position: relative;
  margin: 12px 0px;
  transform: ${props => props.transform};
  transition: ${props => props.transition};
`;

const DragHandle = styled.div`
  cursor: grab;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: 24px;
  height: 24px;
  
  &:hover {
    color: #666;
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const AnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0px;
`;

const AddOption = styled.button`
  background: #efefef;
  border: none;
  cursor: pointer;
  padding: 10px;
  max-width: 600px;
  width: 100%;
`;

const SelectButton = styled(Button)`
  background:#dedede;
  box-shadow: none;
  color: #333;
  flex-shrink: 0;
  font-size: 0.9rem;
  height: 36px;
  padding: 8px 16px;
  position: absolute;
  right: 10px;
  top: 10px;
  margin: 0;
  width: 160px;
  white-space: nowrap;
  @media (max-width: 900px) {
    padding: 12px;
    width: 120px;
  }

  &:hover {
    background: #efefef;
  }

  &:active {
    background: #ddd;
    box-shadow: none;
    color: #555;
  }
`;

const ActionBar = styled.div`
  border-top: 1px solid #dddddd;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const VerticalDivider = styled.div`
  border-left: 1px solid #dddddd;
  height: 28px;
  margin: 0 8px;
`;

const ShortAnswer = styled.p`
  font-size: 14px;
  color: #666;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

const Question = ({ question, selected, setSelected, form, setForm }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const updateQuestion = (newQuestion) => {
    const questionIndex = form.survey_data.questions.findIndex(q => q.id === question.id);    
    const newQuestions = [...form.survey_data.questions];
    newQuestions[questionIndex] = newQuestion;
    
    setForm({
      ...form,
      survey_data: {
        ...form.survey_data,
        questions: newQuestions,
      },
    });
  };
  
  const handleChange = (name, value) => {
    updateQuestion({ ...question, [name]: value });
  };

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const getQuestionType = (responseType) => {
    switch (responseType) {
      case "text":
        return "Text";
      case "checkbox":
        return "Checkbox";
      case "multiple_choice":
        return "Multiple Choice";
      case "linear_scale":
        return "Linear Scale";
      case "select_check_in":
        return "Select Check-in";
      default:
        return "Unknown";
    }
  };

  const handleDropDown = (responseType) => {
    const saveOptions =
      question.responseType === "checkbox" ||
      question.responseType === "multiple_choice";
    const newQuestion = { ...question };
    newQuestion.responseType = responseType;
    if (responseType === "linear_scale") {
      newQuestion.min = 1;
      newQuestion.max = 5;
      newQuestion.minLabel = newQuestion.minLabel || "";
      newQuestion.maxLabel = newQuestion.maxLabel || "";
    } else {
      delete newQuestion.min;
      delete newQuestion.max;
      delete newQuestion.minLabel;
      delete newQuestion.maxLabel;
    }

    if (responseType !== "checkbox" && responseType !== "multiple_choice") {
      delete newQuestion.options;
    } else if (!saveOptions) {
      newQuestion.options = [];
    }
    
    updateQuestion(newQuestion);
    setAnchorEl(null);
  };

  const handleAddOption = () => {
    const newQuestion = { ...question };
    newQuestion.options.push({
      id: uuidv4(),
      value: "",
    });
    updateQuestion(newQuestion);
  };

  const handleDeleteOption = (index) => {
    const newQuestion = { ...question };
    newQuestion.options.splice(index, 1);
    updateQuestion(newQuestion);
  };

  const handleUpdateOption = (index, value) => {
    const newQuestion = { ...question };
    newQuestion.options[index] = value;
    updateQuestion(newQuestion);
  };

  const handleDelete = () => {
    const questionIndex = form.survey_data.questions.findIndex(q => q.id === question.id);
    const newQuestions = [...form.survey_data.questions];
    newQuestions.splice(questionIndex, 1);
    
    setForm({
      ...form,
      survey_data: {
        ...form.survey_data,
        questions: newQuestions,
      },
    });
  };

  const handleRequired = () => {
    const newQuestion = { ...question };
    newQuestion.required = !newQuestion.required;
    updateQuestion(newQuestion);
  };

  // Linear scale is limited to 1-5, may change later
  // const handleMinmax = (min, max) => {
  //   const newQuestion = { ...question };
  //   newQuestion.min = min;
  //   newQuestion.max = max;
  //   updateQuestion(newQuestion);
  // };

  const renderAnswer = () => {
    switch (question.responseType) {
      case "text":
        return (
          <AnswerContainer>
            <ShortAnswer>Short answer text</ShortAnswer>
          </AnswerContainer>
        );
      case "checkbox":
        return (
          <AnswerContainer>
            <CheckboxGroup
              options={question.options}
              selected={selected}
              onDelete={handleDeleteOption}
              onUpdate={handleUpdateOption}
            />
            {selected && (
              <AddOption onClick={() => handleAddOption()}>
                Add Option
              </AddOption>
            )}
          </AnswerContainer>
        );
      case "multiple_choice":
        return (
          <AnswerContainer>
            <RadioGroup
              options={question.options}
              selected={selected}
              onDelete={handleDeleteOption}
              onUpdate={handleUpdateOption}
            />
            {selected && (
              <AddOption onClick={() => handleAddOption()}>
                Add Option
              </AddOption>
            )}
          </AnswerContainer>
        );
      case "linear_scale":
        return (
          <AnswerContainer>
            <LinearScale
              min={question?.min || 1}
              max={question?.max || 5}
              minLabel={question?.minLabel || ""}
              maxLabel={question?.maxLabel || ""}
              // handleMinmax={handleMinmax}
              handleMinLabel={(value) => handleChange("minLabel", value)}
              handleMaxLabel={(value) => handleChange("maxLabel", value)}
              selected={selected}
            />
          </AnswerContainer>
        );
      case "select_check_in":
        return (
          <AnswerContainer>
            <ShortAnswer>Client will select a check-in as a response</ShortAnswer>
          </AnswerContainer>
        );
      default:
        return null;
    }
  };
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <Container
      ref={setNodeRef}
      selected={selected}
      onClick={() => (selected ? null : setSelected(question.id))}
      transition={transition}
      style={style}
    >
      <DragHandle {...attributes} {...listeners}>
        <DragIndicatorIcon style={{rotate: '90deg'}} />
      </DragHandle>
      <SelectButton
        label={getQuestionType(question.responseType)}
        onClick={handleClick}
      />
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleDropDown("multiple_choice")}>
          Multiple Choice
        </MenuItem>
        <MenuItem onClick={() => handleDropDown("checkbox")}>Checkbox</MenuItem>
        <MenuItem onClick={() => handleDropDown("text")}>Text</MenuItem>
        <MenuItem onClick={() => handleDropDown("linear_scale")}>
          Linear Scale
        </MenuItem>
        <MenuItem onClick={() => handleDropDown("select_check_in")}>
          Select Check-in
        </MenuItem>
      </Menu>
      <TextInput
        label="Question"
        type="text"
        placeholder="Question title"
        value={question.title || ""}
        onChange={(e) => handleChange("title", e.target.value)}
      />
      <TextInput
        label="Description"
        type="text"
        placeholder="Question description (optional)"
        value={question.description || ""}
        onChange={(e) => handleChange("description", e.target.value)}
      />
      {renderAnswer()}
      {selected && (
        <ActionBar>
          <DeleteIcon onClick={handleDelete} style={{ cursor: "pointer" }} />
          <VerticalDivider />
          <Toggle
            label="Required"
            checked={question.required}
            onChange={handleRequired}
          />
        </ActionBar>
      )}
    </Container>
  );
};

export default Question;
